import {Button} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {useState} from 'react';
import type {NoteModuleEnum, NoteSimpleResponse} from 'src/api/ProactApiClient';
import {ProfileAvatar} from 'src/app/personnel/ProfileAvatar';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {AdminRoleGuard} from 'src/app/auth/AdminRoleGuard';
import {useDeleteNoteMutation} from 'src/redux/queries/note';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {NoteForm} from './NoteForm';

export interface NotesListRowProps {
  entityId: number;
  noteType: NoteModuleEnum;
  data: NoteSimpleResponse;
  onDeleted: () => void;
  onEdited: () => void;
}
export const NotesListRow = ({
  entityId,
  noteType,
  data,
  onDeleted,
  onEdited,
}: NotesListRowProps) => {
  const {setAlert} = useAlert();
  const [deleteNote, {isLoading: isDeleting}] = useDeleteNoteMutation();
  const [showEdit, setShowEdit] = useState(false);
  const [
    confirmationModalOpened,
    {open: openConfirmationModal, close: closeConfirmationModal},
  ] = useDisclosure(false);

  function handleEditClick(): void {
    setShowEdit(true);
  }

  const handleDeleteClick = async () => {
    try {
      await deleteNote({id: data.id, entityId}).unwrap();
      onDeleted();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <div>
      <Group>
        <ProfileAvatar src={data.creatorAvatarUrl} alt={data.creatorName} />
        <div>
          <Text size="sm">{data.creatorName}</Text>
          <Text size="xs" c="dimmed">
            {data.createdAt.toString().toLongDate()}
          </Text>
        </div>
      </Group>
      <Text pl="xl" pt="sm" size="sm">
        <NoteForm
          mode={showEdit ? 'edit' : 'view'}
          data={data}
          entityId={entityId}
          noteType={noteType}
          onSaved={() => {
            setShowEdit(false);
            onEdited();
          }}
          onCancelled={() => setShowEdit(false)}
        />
      </Text>
      {!showEdit && (
        <AdminRoleGuard>
          <Group pl="md" gap={1}>
            {data.isEditable ? (
              <>
                <Button
                  variant="subtle"
                  color="black"
                  size="xs"
                  onClick={handleEditClick}>
                  Edit
                </Button>
                <Button
                  variant="subtle"
                  color="black"
                  size="xs"
                  loading={isDeleting}
                  onClick={openConfirmationModal}>
                  Delete
                </Button>
                <ConfirmDialog
                  body="Are you sure you want to delete this note?"
                  confirmationButtonColor="red"
                  opened={confirmationModalOpened}
                  onClose={closeConfirmationModal}
                  onConfirm={handleDeleteClick}
                />
              </>
            ) : (
              <Text size="xs" c="dimmed">
                System generated notes cannot be modified.
              </Text>
            )}
          </Group>
        </AdminRoleGuard>
      )}
    </div>
  );
};
